import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Card, Btn, Chip, Input, Select, Field } from "@/components/kit";
import { PencilSimple, Plus, X } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const ROLES = [
  { value: "admin", label: "Administrator" },
  { value: "manager", label: "Manager" },
  { value: "operator", label: "Operator" },
  { value: "inventory", label: "Inventory" },
  { value: "accounts", label: "Accounts" },
  { value: "view_only", label: "View only" },
];
const WORKSHOPS = ["AMSKY", "CRON", "KODAK", "PARIBESHAK"];

const PERMISSION_GROUPS = [
  { label: "Dashboard and reports", items: [
    ["jobs:read", "View jobs and production"],
    ["inventory:read", "View inventory"],
  ]},
  { label: "Job sheets", items: [
    ["jobs:create", "Create job sheets"],
    ["jobs:correct", "Correct job sheets"],
    ["jobs:cancel", "Cancel job sheets"],
  ]},
  { label: "Challans", items: [
    ["challan:create", "Create challans"],
    ["challan:cancel", "Cancel challans"],
  ]},
  { label: "Inventory", items: [
    ["inventory:write", "Update stock"],
    ["inventory:override", "Opening stock and overrides"],
  ]},
  { label: "Purchases and transfers", items: [
    ["purchase:write", "Manage purchases"],
    ["transfer:write", "Manage transfers"],
  ]},
  { label: "Administration", items: [
    ["masters:read", "View master data"],
    ["masters:write", "Edit master data"],
    ["audit:read", "View audit log"],
    ["users:manage", "Manage employees"],
  ]},
];

const ROLE_PRESETS = {
  admin: PERMISSION_GROUPS.flatMap(group => group.items.map(item => item[0])),
  manager: ["masters:read", "jobs:read", "jobs:create", "jobs:correct", "jobs:cancel", "inventory:read", "inventory:write", "purchase:write", "transfer:write", "challan:create", "challan:cancel"],
  operator: ["masters:read", "jobs:read", "jobs:create", "jobs:correct", "jobs:cancel", "challan:create", "inventory:read"],
  inventory: ["masters:read", "jobs:read", "inventory:read", "inventory:write", "purchase:write", "transfer:write"],
  accounts: ["masters:read", "jobs:read", "inventory:read"],
  view_only: ["masters:read", "jobs:read", "inventory:read"],
};

const EMPTY_FORM = {
  name: "",
  employee_id: "",
  email: "",
  password: "",
  role: "operator",
  active: true,
  permissions: ROLE_PRESETS.operator,
  assigned_workshop: "AMSKY",
};

function roleLabel(value) {
  return ROLES.find(role => role.value === value)?.label || value;
}

export default function EmployeeMaster() {
  const [employees, setEmployees] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/users");
      setEmployees(data);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reset = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const edit = employee => {
    setEditingId(employee.id);
    setForm({
      name: employee.name || "",
      employee_id: employee.employee_id || "",
      email: employee.email?.endsWith("@staff.jyotiimage.local") ? "" : (employee.email || ""),
      password: "",
      role: employee.role || "operator",
      active: employee.active !== false,
      permissions: Array.isArray(employee.permissions) ? employee.permissions : (ROLE_PRESETS[employee.role] || []),
      assigned_workshop: employee.assigned_workshop || "AMSKY",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setRole = role => setForm(current => ({
    ...current,
    role,
    permissions: ROLE_PRESETS[role] || [],
  }));

  const togglePermission = permission => setForm(current => ({
    ...current,
    permissions: current.permissions.includes(permission)
      ? current.permissions.filter(item => item !== permission)
      : [...current.permissions, permission],
  }));

  const save = async event => {
    event.preventDefault();
    if (!form.name.trim() || !form.employee_id.trim()) {
      toast.error("Name and employee ID are required.");
      return;
    }
    if (!editingId && !form.password) {
      toast.error("Set an initial password for this employee.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        employee_id: form.employee_id.trim().toUpperCase(),
        email: form.email.trim() || null,
        role: form.role,
        active: form.active,
        permissions: form.permissions,
        assigned_workshop: form.assigned_workshop,
      };
      if (form.password) payload.password = form.password;
      if (editingId) {
        await api.patch("/auth/users/" + editingId, payload);
        toast.success("Employee access updated.");
      } else {
        await api.post("/auth/users", payload);
        toast.success("Employee account created.");
      }
      reset();
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 max-w-[1500px] min-w-0 mx-auto">
      <Toaster richColors position="top-right" />
      <PageHeader
        title="Employee Master"
        subtitle="Create employee IDs and control exactly which parts of the workflow each person can access."
        actions={editingId && <Btn variant="outline" onClick={reset}><Plus size={15} /> New employee</Btn>}
      />

      <div className="grid grid-cols-1 xl:grid-cols-[1.25fr_0.9fr] gap-5">
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <div className="font-heading font-bold">Team access</div>
              <div className="text-xs text-zinc-500 mt-0.5">{employees.length} employee accounts</div>
            </div>
            <Chip tone="info">Role based</Chip>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-2 text-left">Employee ID</th>
                <th className="px-4 py-2 text-left">Employee</th>
                <th className="px-4 py-2 text-left">Role</th>
                <th className="px-4 py-2 text-left">Workshop</th>
                <th className="px-4 py-2 text-left">Access</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2"></th>
              </tr></thead>
              <tbody>
                {employees.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No employee accounts yet.</td></tr>}
                {employees.map(employee => (
                  <tr key={employee.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-3 font-mono font-bold">{employee.employee_id || "-"}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{employee.name}</div>
                      <div className="text-xs text-zinc-500">{employee.email?.endsWith("@staff.jyotiimage.local") ? "Employee ID login" : employee.email}</div>
                    </td>
                    <td className="px-4 py-3">{roleLabel(employee.role)}</td>
                    <td className="px-4 py-3"><Chip tone="info">{employee.assigned_workshop || "Not assigned"}</Chip></td>
                    <td className="px-4 py-3"><Chip tone="neutral">{Array.isArray(employee.permissions) ? employee.permissions.length : "Role"} modules</Chip></td>
                    <td className="px-4 py-3"><Chip tone={employee.active === false ? "bad" : "good"}>{employee.active === false ? "Inactive" : "Active"}</Chip></td>
                    <td className="px-4 py-3 text-right"><Btn variant="ghost" onClick={() => edit(employee)}><PencilSimple size={15} /> Edit</Btn></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-5 h-fit">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h2 className="font-heading font-bold text-lg">{editingId ? "Edit employee" : "Add employee"}</h2>
              <p className="text-xs text-zinc-500 mt-1">Passwords are stored securely and are never displayed.</p>
            </div>
            {editingId && <button onClick={reset} className="text-zinc-400 hover:text-zinc-900" aria-label="Close edit"><X size={18} /></button>}
          </div>

          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Employee name"><Input className="w-full" value={form.name} onChange={event => setForm({...form, name: event.target.value})} /></Field>
              <Field label="Employee ID"><Input className="w-full font-mono uppercase" value={form.employee_id} onChange={event => setForm({...form, employee_id: event.target.value})} placeholder="e.g. JI-014" /></Field>
            </div>
            <Field label="Email (optional)"><Input className="w-full" type="email" value={form.email} onChange={event => setForm({...form, email: event.target.value})} placeholder="employee@example.com" /></Field>
            <Field label={editingId ? "New password (leave blank to keep current)" : "Initial password"}>
              <Input className="w-full" type="password" value={form.password} onChange={event => setForm({...form, password: event.target.value})} autoComplete="new-password" />
            </Field>
            <Field label="Role preset">
              <Select className="w-full" value={form.role} onChange={event => setRole(event.target.value)}>
                {ROLES.map(role => <option key={role.value} value={role.value}>{role.label}</option>)}
              </Select>
            </Field>
            <Field label="Assigned workshop">
              <Select className="w-full" value={form.assigned_workshop} onChange={event => setForm({...form, assigned_workshop: event.target.value})}>
                {WORKSHOPS.map(workshop => <option key={workshop} value={workshop}>{workshop}</option>)}
              </Select>
              <div className="text-xs text-zinc-500 mt-1">Staff can view all workshops, but can create or modify jobs only here. Managers and administrators remain unrestricted.</div>
            </Field>

            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Specific access</div>
              <div className="space-y-3 border border-zinc-200 rounded-sm p-3 max-h-[330px] overflow-y-auto">
                {PERMISSION_GROUPS.map(group => (
                  <div key={group.label}>
                    <div className="text-xs font-bold text-zinc-900 mb-1.5">{group.label}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {group.items.map(([permission, label]) => (
                        <label key={permission} className="flex items-start gap-2 text-xs p-1.5 rounded hover:bg-zinc-50 cursor-pointer">
                          <input type="checkbox" className="mt-0.5 accent-sky-500" checked={form.permissions.includes(permission)} onChange={() => togglePermission(permission)} />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="accent-sky-500" checked={form.active} onChange={event => setForm({...form, active: event.target.checked})} />
              Employee can sign in
            </label>

            <div className="flex gap-2 pt-1">
              <Btn type="submit" disabled={busy}>{busy ? "Saving..." : (editingId ? "Save changes" : "Create employee")}</Btn>
              {editingId && <Btn type="button" variant="outline" onClick={reset}>Cancel</Btn>}
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
